// @flow

import * as React from "react";

import { ReactForm, ReactFormField } from "@adrenalin/react-form";
import { customValidators } from "../../utils/validators";
import scrollToEl from "../../utils/scrollToEl";
import SectionHeader from "../molecules/SectionHeader";
import TextButton from "../atoms/TextButton";
import FileUpload from "../atoms/FileUpload";
import Icon from "../atoms/Icon";

type PropsType = {
    accordions?: [any],
    title?: string | React.Node,
    description?: string,
    onUpdate?: any,
};

const EditableMediaAccordions = (props: PropsType): React.Node => {
    const formRef = React.useRef(null);
    const wrapperRef = React.useRef(null);
    const [formError, setFormError] = React.useState(null);

    const [accordionList, setAccordionList] = React.useState(
        props.defaultValue || [
            {
                image_file: "",
                image: null,
                url: "",
                caption: "",
                type: "image",
                removed: false,
            },
        ]
    );
    const mediaList = [
        {
            label: "Image",
            value: "image",
        },
        {
            label: "Video",
            value: "video",
        },
    ];

    const validateForm = () => {
        if (accordionList.length < 1) {
            setFormError("Please add at least one item to the gallery.");
            if (wrapperRef && wrapperRef.current) {
                // wrapperRef.current.scrollIntoView();
                scrollToEl(wrapperRef.current, 500, -100);
            }
        } else {
            setFormError(null);
            if (formRef.current) {
                formRef.current.submit();
            }
        }
    };
    if (props.customRef && props.customRef.current) {
        props.customRef.current.validateForm = validateForm;
    }

    const addNewAccordion = () => {
        const list = accordionList;
        setAccordionList([
            ...list,
            {
                image_file: "",
                image: null,
                url: "",
                caption: "",
                type: "image",
                removed: false,
            },
        ]);
    };

    const removeAccordion = key => {
        const list = accordionList;
        if (list[key]) list[key].removed = true;
        setAccordionList([...list]);
    };

    const handleMediaEdit = (value, label, id) => {
        const list = accordionList;
        list[id][label] = value;

        // if radio button - type -> refresh data
        if (label === "type") {
            list[id] = {
                image_file: "",
                image: null,
                url: "",
                caption: "",
                alt: "",
                type: value,
            };
        }

        setAccordionList([...list]);
    };

    const handleFileEdit = (value, label, id) => {
        const reader = new FileReader();
        const list = accordionList;
        if (value.target.files.length > 0) {
            const file = value.target.files[0];
            reader.readAsDataURL(file);
            reader.onload = () => {
                list[id].image_file = reader.result;
            };
            list[id].upload_file = file;

            setAccordionList([...list]);
        }
    };

    // update useEffect
    React.useEffect(() => {
        setFormError(null);

        if (props.onUpdate) {
            const formValid = formRef.current.formValid;

            props.onUpdate(
                accordionList.filter(accordion => !accordion.removed),
                formValid && accordionList && accordionList.length
            );
        }
    }, [accordionList]); // eslint-disable-line
    let slideCounter = 0;
    return (
        <div className="accordions" ref={wrapperRef}>
            {props.title || props.description ? (
                <SectionHeader
                    sectionName="accordions"
                    title={props.title}
                    description={props.description}
                />
            ) : null}
            <div className="accordions__content">
                <ReactForm ref={formRef} validators={customValidators}>
                    {accordionList.map((accordion, key) => {
                        if (!accordion.removed) slideCounter++;
                        return accordion.removed ? null : (
                            <div
                                key={key}
                                className={`editable-accordion ${
                                    props.disabled
                                        ? "editable-accordion--disabled"
                                        : ""
                                }`}
                            >
                                <div className="editable-accordion__header">
                                    <div className="editable-accordion__header__title">
                                        Slide {slideCounter}
                                        {key === 0 ? " - Cover image" : null}
                                    </div>
                                    {key > 0 ? (
                                        <TextButton
                                            type="button"
                                            className="editable-accordion__header__remove"
                                            onClick={() => removeAccordion(key)}
                                            disabled={props.disabled}
                                        >
                                            Remove
                                        </TextButton>
                                    ) : null}
                                </div>
                                <div className="editable-accordion__body">
                                    {key > 0 ? (
                                        <div className="editable-media-accordion__body__media-type">
                                            {mediaList.map((media, index) => {
                                                return (
                                                    <ReactFormField
                                                        key={index}
                                                        className="react-form__field--reverse"
                                                        type="radio"
                                                        label={media.label}
                                                        name={`mediaType_${key}`}
                                                        value={media.value}
                                                        onChange={e =>
                                                            handleMediaEdit(
                                                                e.target.value,
                                                                "type",
                                                                key
                                                            )
                                                        }
                                                        defaultChecked={
                                                            accordion.url &&
                                                            accordion.url
                                                                .length > 0
                                                                ? media.value ===
                                                                  "video"
                                                                : media.value ===
                                                                  "image"
                                                        }
                                                        disabled={
                                                            props.disabled
                                                        }
                                                    />
                                                );
                                            })}
                                        </div>
                                    ) : null}
                                    {accordion.type === "image" ? (
                                        <div>
                                            <div className="editable-accordion__body__label">
                                                File upload
                                            </div>
                                            <div className="editable-accordion__body__desc">
                                                <ul>
                                                    <li>
                                                        Image files must be{" "}
                                                        {"<"}250KB and in PNG or
                                                        JPEG format.
                                                    </li>
                                                    <li>
                                                        Images must be either
                                                        1032 x 1032 (1:1) or
                                                        1500 x 1000 (3:2).
                                                    </li>
                                                    <li>
                                                        Images should not
                                                        contain any text.
                                                    </li>
                                                    <li>
                                                        Landscape images may be
                                                        cropped to Square on
                                                        some pages.
                                                    </li>
                                                </ul>
                                            </div>
                                            <ReactFormField
                                                type="file"
                                                validators={["maxFileSize:5"]}
                                                accept={".jpg, .jpeg, .png"}
                                                required={!accordion.fileName} // only required when no exisitng image
                                                fieldComponent={FileUpload}
                                                name={`imageFile_${key}`}
                                                placeholder={
                                                    !accordion.image_file
                                                        ? accordion.fileName
                                                        : ""
                                                }
                                                preview={
                                                    accordion.preview ||
                                                    accordion.image_file
                                                }
                                                onChange={e =>
                                                    handleFileEdit(
                                                        e,
                                                        "image_file",
                                                        key
                                                    )
                                                }
                                                disabled={props.disabled}
                                            />
                                        </div>
                                    ) : (
                                        <ReactFormField
                                            type="text"
                                            label="YouTube URL"
                                            name={`videoUrl_${key}`}
                                            value={accordion.url}
                                            onChange={e =>
                                                handleMediaEdit(
                                                    e.target.value,
                                                    "url",
                                                    key
                                                )
                                            }
                                            required={true}
                                            validators={["youtubeurl"]}
                                            disabled={props.disabled}
                                        />
                                    )}
                                    <div className="editable-media-accordion__content">
                                        <div>
                                            <div className="editable-accordion__body__label">
                                                Caption
                                            </div>
                                            <ReactFormField
                                                type="textarea"
                                                name={`caption_${key}`}
                                                value={accordion.caption}
                                                required={true}
                                                onChange={e =>
                                                    handleMediaEdit(
                                                        e.target.value,
                                                        "caption",
                                                        key
                                                    )
                                                }
                                                disabled={props.disabled}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {!props.disabled &&
                    (!props.maxItems ||
                        accordionList.filter(accordion => !accordion.removed)
                            .length < props.maxItems) ? ( // max 5 items can be added
                        <TextButton
                            type="button"
                            className="editable-accordion__add"
                            onClick={addNewAccordion}
                        >
                            <Icon name="plus-circle" />
                            <span>Add another slide</span>
                        </TextButton>
                    ) : null}

                    {formError ? (
                        <div class="react-form__field__errors">
                            <div class="react-form__field__errors__item">
                                {formError}
                            </div>
                        </div>
                    ) : null}
                </ReactForm>
            </div>
        </div>
    );
};

export default EditableMediaAccordions;
